import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { pickText } from "@heylife/i18n";
import { api, fmtDate, scopeQuery, useScope, type Localized } from "../api";
import { useT } from "../i18n";
import { isValidSlug, slugify, useSlug } from "../slug";
import { ConfirmDelete, Field, LocalizedField, Msg, SlugInput, Toggle, THEMES, useFlash } from "../ui";

/** Content → Challenges (templates) and Waves (cohorts) tabs. */

interface ChallengeRow {
  id: string;
  churchId: string | null;
  slug: string;
  title: Localized;
  description: Localized | null;
  dailyPrompt: Localized | null;
  durationDays: number;
  lifeArea: string;
  theme: string;
  coverImage: string | null;
  dayPoints: number;
  completionPoints: number;
  published: boolean;
  cohortCount: number;
  participants: number;
  completions: number;
}

export function Challenges() {
  const { t, locale } = useT();
  const scope = useScope();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<ChallengeRow | "new" | null>(null);
  const q = useQuery({ queryKey: ["admin", "content", "challenges", scope.churchId], queryFn: () => api<{ challenges: ChallengeRow[]; lifeAreas: string[] }>(`/admin/content/challenges${scopeQuery(scope, "scope")}`) });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin", "content", "challenges"] });
  const del = useMutation({ mutationFn: (id: string) => api(`/admin/content/challenges/${id}`, { method: "DELETE" }), onSuccess: invalidate });

  return (
    <section className="panel">
      <div className="toolbar">
        <span />
        <button className="btn small" onClick={() => setEditing("new")}>
          {t("admin.content.newChallenge")}
        </button>
      </div>
      {editing ? <ChallengeForm challenge={editing === "new" ? undefined : editing} lifeAreas={q.data?.lifeAreas ?? []} onClose={() => setEditing(null)} onSaved={invalidate} /> : null}
      {q.data?.challenges.length === 0 ? <p className="muted">{t("admin.content.noRows")}</p> : null}
      <div className="pathgrid">
        {q.data?.challenges.map((c) => (
          <div key={c.id} className={`pathcard theme-${c.theme}`}>
            <div className="pathcard-top">
              <span className="pill">{c.churchId ? t("admin.scope.church") : t("admin.scope.global")}</span>
              <span className={`pill ${c.published ? "live" : ""}`}>{c.published ? t("admin.common.published") : t("admin.common.draft")}</span>
            </div>
            <h3>{pickText(c.title, locale)}</h3>
            <p className="muted">
              {t("common.days", { count: c.durationDays })} · {t(`challenges.area.${c.lifeArea}`)} · {t("admin.content.totalOnCompletion", { points: c.durationDays * c.dayPoints + c.completionPoints })}
            </p>
            <div className="stats">
              <span>
                <strong>{c.cohortCount}</strong> {t("admin.content.cohorts")}
              </span>
              <span>
                <strong>{c.participants}</strong> {t("admin.content.participants")}
              </span>
              <span>
                <strong>{c.completions}</strong> {t("admin.content.completions")}
              </span>
            </div>
            <div className="actions">
              <button className="btn small ghost" onClick={() => setEditing(c)}>
                {t("admin.common.edit")}
              </button>
              <ConfirmDelete onConfirm={() => del.mutate(c.id)} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function ChallengeForm({ challenge, lifeAreas, onClose, onSaved }: { challenge?: ChallengeRow; lifeAreas: string[]; onClose: () => void; onSaved: () => void }) {
  const { t } = useT();
  const scope = useScope();
  const [f, setF] = useState<Partial<ChallengeRow>>(challenge ?? { slug: "", title: {}, description: {}, dailyPrompt: {}, durationDays: 7, lifeArea: "spirit", theme: "carmine", dayPoints: 10, completionPoints: 50, published: true });
  const slugField = useSlug(challenge?.slug ?? "");
  const [flash, run, busy] = useFlash();
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const slug = slugify(slugField.slug, { trim: true });
    const body = {
      slug,
      title: f.title,
      description: f.description,
      dailyPrompt: f.dailyPrompt,
      durationDays: Number(f.durationDays),
      lifeArea: f.lifeArea,
      theme: f.theme,
      coverImage: f.coverImage || null,
      dayPoints: Number(f.dayPoints),
      completionPoints: Number(f.completionPoints),
      published: Boolean(f.published),
    };
    if (
      await run(async () => {
        if (!isValidSlug(slug)) throw new Error(t("admin.errors.slug"));
        return challenge ? api(`/admin/content/challenges/${challenge.id}`, { method: "PATCH", json: body }) : api(`/admin/content/challenges${scopeQuery(scope, "scope")}`, { method: "POST", json: body });
      })
    ) {
      onSaved();
      onClose();
    }
  };
  return (
    <form className="card form" onSubmit={save}>
      <div className="two-col">
        <Field label={t("admin.content.slug")}>
          <SlugInput value={slugField.slug} onValue={slugField.setSlugInput} onBlurValue={slugField.blurSlug} />
        </Field>
        <Field label={t("admin.content.lifeArea")}>
          <select value={f.lifeArea} onChange={(e) => setF({ ...f, lifeArea: e.target.value })}>
            {lifeAreas.map((a) => (
              <option key={a} value={a}>
                {t(`challenges.area.${a}`)}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <LocalizedField
        label={t("admin.content.challengeTitle")}
        required
        value={f.title}
        onChange={(title) => {
          setF({ ...f, title });
          slugField.syncFrom(title.de || title.en || "");
        }}
      />
      <LocalizedField label={t("admin.content.description")} multiline value={f.description} onChange={(description) => setF({ ...f, description })} />
      <LocalizedField label={t("admin.content.dailyPrompt")} value={f.dailyPrompt} onChange={(dailyPrompt) => setF({ ...f, dailyPrompt })} />
      <div className="two-col">
        <Field label={t("admin.content.durationDays")}>
          <input type="number" min={1} max={366} value={f.durationDays ?? 7} onChange={(e) => setF({ ...f, durationDays: Number(e.target.value) })} />
        </Field>
        <Field label={t("admin.content.theme")}>
          <select value={f.theme} onChange={(e) => setF({ ...f, theme: e.target.value })}>
            {THEMES.map((th) => (
              <option key={th}>{th}</option>
            ))}
          </select>
        </Field>
        <Field label={t("admin.content.dayPoints")}>
          <input type="number" min={0} value={f.dayPoints ?? 10} onChange={(e) => setF({ ...f, dayPoints: Number(e.target.value) })} />
        </Field>
        <Field label={t("admin.content.completionPoints")}>
          <input type="number" min={0} value={f.completionPoints ?? 50} onChange={(e) => setF({ ...f, completionPoints: Number(e.target.value) })} />
        </Field>
      </div>
      <Field label={t("admin.content.coverImage")}>
        <input value={f.coverImage ?? ""} onChange={(e) => setF({ ...f, coverImage: e.target.value })} placeholder="https://…" />
      </Field>
      <Toggle label={t("admin.common.published")} checked={Boolean(f.published)} onChange={(published) => setF({ ...f, published })} />
      <div className="actions">
        <button className="btn small" disabled={busy}>
          {t("admin.common.save")}
        </button>
        <button type="button" className="btn small ghost" onClick={onClose}>
          {t("admin.common.cancel")}
        </button>
      </div>
      <Msg msg={flash} />
    </form>
  );
}

/* ------------------------------------------------------------------ waves */

interface CohortRow {
  id: string;
  code: string;
  startDate: string;
  title: Localized;
  theme: string;
  durationDays: number;
  state: "upcoming" | "running" | "completed" | "archived";
  members: number;
  completed: number;
  checkIns: number;
  checkInsToday: number;
  messages: number;
}

export function Cohorts() {
  const { t, locale } = useT();
  const scope = useScope();
  const q = useQuery({ queryKey: ["admin", "cohorts", scope.churchId], queryFn: () => api<{ cohorts: CohortRow[] }>(`/admin/cohorts${scopeQuery(scope, "church")}`), refetchInterval: 30_000 });
  return (
    <section className="panel">
      <p className="muted">{t("admin.content.cohortsBody")}</p>
      {q.data?.cohorts.length === 0 ? <p className="muted">{t("admin.content.noCohorts")}</p> : null}
      <table className="grid">
        <thead>
          <tr>
            <th>{t("admin.content.challengeTitle")}</th>
            <th>{t("admin.content.code")}</th>
            <th>{t("admin.content.start")}</th>
            <th>{t("admin.content.state")}</th>
            <th>{t("admin.content.participants")}</th>
            <th>{t("admin.today.checkIns")}</th>
            <th>{t("admin.content.messages")}</th>
          </tr>
        </thead>
        <tbody>
          {q.data?.cohorts.map((c) => (
            <tr key={c.id}>
              <td>
                <strong>{pickText(c.title, locale)}</strong>
              </td>
              <td>
                <code>{c.code}</code>
              </td>
              <td>{fmtDate(c.startDate, locale)}</td>
              <td>
                <span className={`pill ${c.state === "running" ? "live" : ""}`}>{c.state}</span>
              </td>
              <td>
                {c.members} · {c.completed} ✓
              </td>
              <td>
                {c.checkIns} <span className="muted">({c.checkInsToday} {t("admin.content.checkInsToday")})</span>
              </td>
              <td>{c.messages}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
